import React, { useEffect, useMemo } from 'react';
import { Button, Col, Flex, Form, message, Row, Space, Tooltip } from 'antd';
import { useDispatch, useIntl, useSelector } from 'umi';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import InfoCard from './InfoCard';
import LeadLocale from '@/components/Lead/LeadLocale';
import { F } from '@/utils/intl';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { setExtraFieldValueRequest } from '@/services/api/extraFieldType';
import { selectExtraFieldTypesList } from '@/selectors/extraFieldsSelectors';
import { mergeAutoAttachedExtraFields } from '@/utils/extraFields';
import isEmpty from 'lodash/isEmpty';
import { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import PhoneNumberInput from '@/components/format/PhoneNumberInput';
import styles from './CustomerInfoCard.less';

export interface CustomerInfoCardProps {
  customerInfo: any;
  leadId: string;
  extraFields?: any[];
  onRefresh?: () => void;
}

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({
  customerInfo,
  leadId,
  extraFields = [],
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [loading, setLoading] = React.useState(false);
  const allExtraFieldTypes = useSelector(selectExtraFieldTypesList);

  const attachedExtraFields = useMemo(() => {
    const withValues = extraFields.filter((field) => !isEmpty(field.values));
    return mergeAutoAttachedExtraFields(withValues, allExtraFieldTypes, 'customer');
  }, [extraFields, allExtraFieldTypes]);

  useEffect(() => {
    dispatch({ type: 'extraFields/fetchExtraFieldTypes' });
  }, [dispatch]);

  useEffect(() => {
    if (customerInfo) {
      form.setFieldsValue({
        firstname: customerInfo.firstname || '',
        lastname: customerInfo.lastname || '',
        email: customerInfo.email || '',
        locale: customerInfo.locale || '',
      });
    }
  }, [customerInfo, form]);

  const selectedFieldIds = useMemo(() => {
    return attachedExtraFields.map((field) => field.uuid);
  }, [attachedExtraFields]);

  const handleFieldSelect = async (field: ExtraFieldTypeModel) => {
    setLoading(true);
    try {
      await setExtraFieldValueRequest({
        params: { extraFieldTypeId: field.uuid, leadId },
        data: { value: field.field_type === 'bool' ? false : null },
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: 'pages.extraFieldType.addedError',
          defaultMessage: 'Error adding field',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSubmit = async (fieldName: string, value: any) => {
    setLoading(true);
    try {
      dispatch({
        type: 'leads/edit',
        payload: { leadId, [`customer.${fieldName}`]: value },
        then: () => {
          setLoading(false);
          if (onRefresh) onRefresh();
        },
      });
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <InfoCard
      title={
        <Flex className={styles.fullWidth} align={'center'} justify={'space-between'}>
          <Space>
            <UserOutlined />
            <F id={'pages.lead.customer_information'} defaultMessage={'Customer Information'} />
          </Space>
          <Tooltip
            title={
              <F
                id={'pages.lead.customer_information_tooltip'}
                defaultMessage={
                  'Customers can have multiple Projects. When changing Customer data, it will change for all Projects.'
                }
              />
            }
          >
            <Button type={'text'} icon={<InfoCircleOutlined />} />
          </Tooltip>
        </Flex>
      }
      variant="blue"
    >
      <ProForm form={form} autoComplete="off" size="small" submitter={false} variant={'underlined'}>
        <Row gutter={8}>
          <Col span={12}>
            <ProFormText
              label={intl.formatMessage({ id: 'pages.customers.firstName', defaultMessage: 'First Name' })}
              name="firstname"
              rules={[{ required: true }]}
              fieldProps={{
                onBlur: async (e) => {
                  const value = e.target.value;
                  try {
                    await form.validateFields(['firstname']);
                    if (value !== customerInfo.firstname) handleFieldSubmit('firstname', value);
                  } catch {}
                },
              }}
            />
          </Col>
          <Col span={12}>
            <ProFormText
              label={intl.formatMessage({ id: 'pages.customers.lastName', defaultMessage: 'Last Name' })}
              name="lastname"
              rules={[{ required: true }]}
              fieldProps={{
                onBlur: async (e) => {
                  const value = e.target.value;
                  try {
                    await form.validateFields(['lastname']);
                    if (value !== customerInfo.lastname) handleFieldSubmit('lastname', value);
                  } catch {}
                },
              }}
            />
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <ProFormText
              label={intl.formatMessage({ id: 'common.attribute.email', defaultMessage: 'Email' })}
              name="email"
              rules={[{ required: true }, { type: 'email' }]}
              fieldProps={{
                onBlur: async (e) => {
                  const value = e.target.value;
                  try {
                    await form.validateFields(['email']);
                    if (value !== customerInfo.email) handleFieldSubmit('email', value);
                  } catch {}
                },
              }}
            />
          </Col>
          <Col span={12}>
            <ProForm.Item
              label={intl.formatMessage({ id: 'common.attribute.phone', defaultMessage: 'Phone' })}
            >
              <PhoneNumberInput
                value={customerInfo?.phone}
                onChange={(e164) => {
                  if (e164 !== customerInfo?.phone) handleFieldSubmit('phone', e164);
                }}
              />
            </ProForm.Item>
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <ProForm.Item
              label={
                <F id={'pages.leads.filters.locale.placeholder'} defaultMessage={'Language'} />
              }
            >
              <LeadLocale
                lead={{ customer: customerInfo, id: leadId }}
                selectProps={{ bordered: true }}
              />
            </ProForm.Item>
          </Col>
        </Row>

        {attachedExtraFields && attachedExtraFields.length > 0 && (
          <Row gutter={8}>
            <Col span={24}>
              <DynamicExtraFieldsForm
                fields={attachedExtraFields}
                leadId={leadId}
                labelWidth={240}
                formProps={{ layout: 'vertical' }}
                removable
                grid
                onRefresh={onRefresh}
              />
            </Col>
          </Row>
        )}

        <Row gutter={8}>
          <Col span={24}>
            <ExtraFieldSelector
              relatedTo="customer"
              selectedFieldIds={selectedFieldIds}
              onFieldSelect={handleFieldSelect}
              onFieldCreated={handleFieldSelect}
            />
          </Col>
        </Row>
      </ProForm>
    </InfoCard>
  );
};

export default CustomerInfoCard;
